import { useState, useCallback, useEffect, useRef } from "react";
import { validateTagsFile, importTagsService } from "modules/tags/services/tagService";
export const useFormImportacionTags = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validating, setValidating] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [duplicateInFile, setDuplicateInFile] = useState([]);
  const [duplicateInDatabase, setDuplicateInDatabase] = useState([]);
  const [validationDetails, setValidationDetails] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const mounted = useRef(true),
    sequence = useRef(0),
    validatedFile = useRef(null),
    importingNow = useRef(false),
    validatingNow = useRef(false),
    fileNow = useRef(null),
    validationRequest = useRef(null),
    importRequest = useRef(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      ++sequence.current;
      validationRequest.current?.abort();
      importRequest.current?.abort();
    };
  }, []);
  const getCurrentUsername = useCallback(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("user") || "null");
      return parsed?.user?.username || parsed?.username || parsed?.name || "Usuario";
    } catch {
      return "Usuario";
    }
  }, []);
  const clearValidation = () => {
    validatedFile.current = null;
    setValidationErrors([]);
    setDuplicateInFile([]);
    setDuplicateInDatabase([]);
    setValidationDetails(null);
    setImportProgress(0);
  };
  const handleFileChange = async (file) => {
    if (importingNow.current) return;
    const id = ++sequence.current;
    validationRequest.current?.abort();
    validationRequest.current = new AbortController();
    clearValidation();
    fileNow.current = file;
    setSelectedFile(file);
    setImportResult(null);
    setShowConfirmModal(false);
    setValidating(true);
    validatingNow.current = true;
    try {
      const result = await validateTagsFile(file, { signal: validationRequest.current.signal });
      if (!mounted.current || id !== sequence.current) return;
      setValidationDetails({
        totalRows: result.totalRows || 0,
        validRows: result.validCount || 0,
        invalidRows: result.invalidCount || 0,
      });
      setValidationErrors(result.errors || []);
      setDuplicateInFile(result.duplicateInFile || []);
      setDuplicateInDatabase(result.duplicateInDatabase || []);
      validatedFile.current = result.isValid ? file : null;
      setSnackbar({
        open: true,
        message: result.isValid
          ? `${result.validCount} registros validados correctamente.`
          : "No se puede importar. Revisa los errores del archivo o de la consulta de duplicados.",
        severity: result.isValid ? "success" : "error",
      });
    } catch (error) {
      if (!mounted.current || id !== sequence.current) return;
      setValidationErrors([error.message || "Error al validar el archivo"]);
    } finally {
      if (mounted.current && id === sequence.current) {
        setValidating(false);
        validatingNow.current = false;
      }
    }
  };
  const handleImport = async (onImportSuccess, onError) => {
    const file = fileNow.current;
    if (importingNow.current || validatingNow.current || !file || validatedFile.current !== file)
      return;
    importingNow.current = true;
    importRequest.current = new AbortController();
    setImporting(true);
    setShowConfirmModal(true);
    setImportResult(null);
    setImportProgress(0);
    try {
      const result = await importTagsService(
        file,
        (progress) => {
          if (mounted.current) setImportProgress(progress);
        },
        { signal: importRequest.current.signal }
      );
      if (!mounted.current) return;
      setImportResult(result);
      if (result.success) {
        validatedFile.current = null;
        onImportSuccess?.(result);
      } else {
        if (result.validationErrors) setValidationErrors(result.validationErrors);
        setDuplicateInFile(result.duplicateInFile || []);
        setDuplicateInDatabase(result.duplicateInDatabase || []);
        onError?.(result.error);
      }
      setSnackbar({
        open: true,
        message: result.success
          ? result.message || "Importación confirmada."
          : result.error || "No se pudo confirmar la importación.",
        severity: result.success ? "success" : "error",
      });
    } catch (error) {
      if (!mounted.current) return;
      setImportResult({ success: false, error: error.message });
      onError?.(error.message);
    } finally {
      importingNow.current = false;
      if (mounted.current) setImporting(false);
    }
  };
  const clearSelectedFile = () => {
    if (importingNow.current) return;
    ++sequence.current;
    validationRequest.current?.abort();
    validatingNow.current = false;
    setValidating(false);
    fileNow.current = null;
    setSelectedFile(null);
    clearValidation();
    const input = document.getElementById("file-input");
    if (input) input.value = "";
  };
  const handleCloseModal = () => {
    if (importingNow.current) return;
    setShowConfirmModal(false);
    if (importResult?.success) clearSelectedFile();
  };
  const handleCloseSnackbar = () => setSnackbar((previous) => ({ ...previous, open: false }));
  return {
    selectedFile,
    importing,
    importResult,
    showConfirmModal,
    validationErrors,
    validating,
    snackbar,
    loading: false,
    importProgress,
    duplicateInFile,
    duplicateInDatabase,
    validationDetails,
    getCurrentUsername,
    handleFileChange,
    handleImport,
    handleCloseModal,
    clearSelectedFile,
    handleCloseSnackbar,
    setSnackbar,
  };
};
